import { useEffect, useRef, useState } from "react";

import { useRouter } from "@tanstack/react-router";
import { util } from "zod/v4/core";

import { PageContainer } from "@/modules/shared/components/PageContainer";
import { ONE_SECOND } from "@/modules/shared/consts/time";
import { useRefetchOnTabFocus } from "@/modules/shared/hooks/useRefetchOnTabFocus";
import { SHORT_URL_EVENTS } from "@/modules/tracking/consts/Events";
import {
  ShortUrlFlow,
  ShortUrlRenderState,
} from "@/modules/tracking/consts/ShortUrl";
import { usePageLoaded } from "@/modules/tracking/hooks/usePageLoaded";
import { useShortUrlFlowAbandonment } from "@/modules/tracking/hooks/useShortUrlFlowAbandonment";
import { getShortUrlRenderState } from "@/modules/tracking/utils/getShortUrlRenderState";
import { updateShortUrlFlowScreen } from "@/modules/tracking/utils/shortUrlFlowSession";
import { trackShortUrlEvent } from "@/modules/tracking/utils/trackShortUrlEvent";
import { Route } from "@/routes/$sessionCode/index";

import { Join_Early } from "../../components/EarlyJoin";
import { Join_Ended } from "../../components/Ended";
import { Join_Missed } from "../../components/Missed";
import { Join_NothingYet } from "../../components/NothingYet";
import { SessionJoinState } from "../../enums/SessionJoinState";
import { getSessionJoinState } from "../../utils/getSessionJoinState";
import { LAST_JOINED_MEETING_URL_KEY } from "../../utils/joinStorageKeys";

const POLLING_INTERVAL = 30 * ONE_SECOND;
const SLOW_POLLING_INTERVAL = 5 * 60 * ONE_SECOND;
const JOIN_WINDOW_SECONDS = 15 * 60;

export function JoinScreen() {
  useRefetchOnTabFocus();
  useShortUrlFlowAbandonment();

  const {
    data: { state, secondsUntilStart, rescheduleUuid, bookNewSession },
    isFromCache,
  } = Route.useLoaderData();
  const router = useRouter();
  const { sessionCode } = Route.useParams();

  const joinState = getSessionJoinState(state);
  const renderState = getShortUrlRenderState(joinState);

  const [previouslyJoinedUrl] = useState(() =>
    typeof window !== "undefined"
      ? localStorage.getItem(LAST_JOINED_MEETING_URL_KEY)
      : null,
  );

  usePageLoaded({
    flow: ShortUrlFlow.Join,
    session_id: null,
    coach_id: null,
    time_until_session_start_minutes: Math.round(secondsUntilStart / 60),
    is_first_visit: !previouslyJoinedUrl,
    has_joined_previously: !!previouslyJoinedUrl,
  });

  const trackedRenderStates = useRef(new Set<ShortUrlRenderState>());

  useEffect(() => {
    if (trackedRenderStates.current.has(renderState)) return;
    trackedRenderStates.current.add(renderState);

    updateShortUrlFlowScreen(renderState);
    trackShortUrlEvent(SHORT_URL_EVENTS.STATE_RENDERED, {
      flow: ShortUrlFlow.Join,
      session_id: null,
      state: renderState,
      time_offset_minutes: Math.round(-secondsUntilStart / 60),
    });
  }, [renderState, secondsUntilStart]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    if (isFromCache) {
      // Keep trying even when repeated fallbacks return the same countdown.
      interval = setInterval(() => {
        router.invalidate();
      }, 5 * ONE_SECOND);
    } else if (secondsUntilStart > JOIN_WINDOW_SECONDS) {
      interval = setInterval(() => {
        router.invalidate();
      }, SLOW_POLLING_INTERVAL);
    }

    if (
      !isFromCache &&
      secondsUntilStart > 0 &&
      secondsUntilStart <= JOIN_WINDOW_SECONDS
    ) {
      timeout = setTimeout(() => {
        router.invalidate();
      }, secondsUntilStart * ONE_SECOND);
    }

    if (!isFromCache && secondsUntilStart === 0) {
      interval = setInterval(() => {
        router.invalidate();
      }, POLLING_INTERVAL);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [isFromCache, secondsUntilStart, router]);

  switch (joinState) {
    case SessionJoinState.NothingYet:
      return <Join_NothingYet />;
    case SessionJoinState.Early:
      return <Join_Early />;
    case SessionJoinState.JoinNow:
      return <PageContainer variant="constrained" />;
    case SessionJoinState.ReJoin:
      return <PageContainer variant="constrained" />;
    case SessionJoinState.Missed:
      return <Join_Missed rescheduleUuid={rescheduleUuid} />;
    case SessionJoinState.Ended:
      return (
        <Join_Ended
          bookNewSessionData={bookNewSession}
          sessionCode={sessionCode}
        />
      );
    default:
      util.assertNever(joinState);
  }
}
